import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { currentQuarter, quarterOptions, quarterLabel } from "@/lib/periods";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Star, ThumbsUp, MessageSquare, ExternalLink, Activity } from "lucide-react";

function AggCard({ icon: Icon, label, value, suffix }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon className="h-5 w-5 text-accent" />
      </div>
      <div className="mt-3 font-display text-3xl font-bold">{value}<span className="text-lg text-muted-foreground">{suffix}</span></div>
    </Card>
  );
}

export default function Surveys() {
  const [quarter, setQuarter] = useState(currentQuarter());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/surveys", { params: { quarter } }).then((r) => setData(r.data)).catch(() => {});
  }, [quarter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8 animate-fade-up" data-testid="surveys-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pesquisa de Satisfação</h1>
          <p className="text-muted-foreground mt-1">Feedback trimestral de líderes e participantes dos PGDs.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Trimestre</Label>
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="mt-1.5 w-48" data-testid="survey-quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {quarterOptions().map((q) => <SelectItem key={q} value={q}>{quarterLabel(q)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Link to="/pesquisa" target="_blank">
            <Button variant="outline" className="gap-2" data-testid="open-survey-form-button"><ExternalLink className="h-4 w-4" /> Formulário</Button>
          </Link>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <AggCard icon={MessageSquare} label="Respostas" value={data.aggregate.count} />
            <AggCard icon={Star} label="Satisfação" value={data.aggregate.avg_satisfaction} suffix="/5" />
            <AggCard icon={Activity} label="Engajamento" value={data.aggregate.avg_engagement} suffix="/5" />
            <AggCard icon={ThumbsUp} label="Recomendam" value={data.aggregate.recommend_pct} suffix="%" />
          </div>

          <Card className="p-6" data-testid="survey-by-group">
            <h3 className="font-display text-lg font-semibold mb-4">Satisfação por Grupo</h3>
            {data.by_group.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Sem dados por grupo neste trimestre.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.by_group.map((g) => (
                  <div key={g.group_id || "none"} className="rounded-xl border border-border p-4" data-testid="survey-group-card">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm">{g.group_name}</span>
                      <Badge variant="secondary">{g.count}</Badge>
                    </div>
                    <div className="mt-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-accent" /> Satisfação</span><span className="font-medium">{g.avg_satisfaction}/5</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-accent" /> Engajamento</span><span className="font-medium">{g.avg_engagement}/5</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><ThumbsUp className="h-3.5 w-3.5 text-accent" /> Recomendam</span><span className="font-medium">{g.recommend_pct}%</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg font-semibold mb-4">Comentários</h3>
            {data.responses.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">
                Nenhuma resposta neste trimestre. Compartilhe o link do formulário com líderes e participantes.
              </p>
            ) : (
              <div className="space-y-3">
                {data.responses.map((r) => (
                  <div key={r.id} className="rounded-lg border border-border p-4" data-testid="survey-response-card">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge variant={r.respondent_type === "lider" ? "default" : "secondary"}>
                        {r.respondent_type === "lider" ? "Líder" : "Participante"}
                      </Badge>
                      {r.name && <span className="text-sm font-medium">{r.name}</span>}
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Star className="h-3 w-3 text-accent" /> {r.satisfaction}/5</span>
                    </div>
                    {r.comments ? <p className="text-sm text-muted-foreground">{r.comments}</p> : <p className="text-sm text-muted-foreground italic">Sem comentário.</p>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
